import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  ArrowLeftRight, Search, Filter, ArrowUpRight, ArrowDownRight, Calendar
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '@/utils/errorHandler';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function BankingTransactions() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterCategorized, setFilterCategorized] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    fetchTransactions();
  }, [filterType, filterCategorized]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterType !== 'all') params.transaction_type = filterType;
      if (filterCategorized === 'yes') params.is_categorized = true;
      if (filterCategorized === 'no') params.is_categorized = false;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      
      const response = await axios.get(`${API_URL}/banking/transactions`, { params });
      setTransactions(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load transactions'));
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR'
    }).format(amount || 0);
  };

  const filteredTransactions = transactions.filter(txn =>
    txn.description?.toLowerCase().includes(search.toLowerCase()) ||
    txn.reference?.toLowerCase().includes(search.toLowerCase()) ||
    txn.category?.toLowerCase().includes(search.toLowerCase())
  );

  const totalCredits = filteredTransactions
    .filter(t => t.transaction_type === 'credit')
    .reduce((sum, t) => sum + t.amount, 0);
  
  const totalDebits = filteredTransactions
    .filter(t => t.transaction_type === 'debit')
    .reduce((sum, t) => sum + t.amount, 0);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="banking-transactions">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Transactions Ledger</h1>
          <p className="text-muted-foreground mt-1">All bank transactions in chronological order</p>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-green-50 border-green-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <ArrowUpRight className="h-8 w-8 text-green-600" />
                <div>
                  <p className="text-sm text-green-700">Total Credits</p>
                  <p className="text-2xl font-bold text-green-700">{formatCurrency(totalCredits)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-red-50 border-red-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <ArrowDownRight className="h-8 w-8 text-red-600" />
                <div>
                  <p className="text-sm text-red-700">Total Debits</p>
                  <p className="text-2xl font-bold text-red-700">{formatCurrency(totalDebits)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <ArrowLeftRight className="h-8 w-8 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Net</p>
                  <p className={`text-2xl font-bold ${totalCredits - totalDebits >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(totalCredits - totalDebits)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search transactions..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger className="w-36">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="credit">Credits Only</SelectItem>
                    <SelectItem value="debit">Debits Only</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filterCategorized} onValueChange={setFilterCategorized}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Categorized" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="yes">Categorized</SelectItem>
                    <SelectItem value="no">Uncategorized</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-36"
                  placeholder="From"
                />
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-36"
                  placeholder="To"
                />
                <Button onClick={fetchTransactions} variant="secondary">
                  Apply
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Transactions Table */}
        <Card>
          <CardHeader>
            <CardTitle>Transactions</CardTitle>
            <CardDescription>{filteredTransactions.length} transactions</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-12 text-muted-foreground">Loading...</div>
            ) : filteredTransactions.length === 0 ? (
              <div className="text-center py-12">
                <ArrowLeftRight className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                <h3 className="text-lg font-medium mb-2">No transactions found</h3>
                <p className="text-muted-foreground mb-4">Import a bank statement to see transactions</p>
                <Button variant="outline" asChild>
                  <a href="/admin/banking/import">Import Statement</a>
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Reference</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredTransactions.map((txn) => (
                      <TableRow key={txn.id}>
                        <TableCell className="text-muted-foreground whitespace-nowrap">
                          {txn.date}
                        </TableCell>
                        <TableCell className="max-w-xs truncate" title={txn.description}>
                          {txn.description}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {txn.reference || '-'}
                        </TableCell>
                        <TableCell>
                          {txn.is_categorized ? (
                            <Badge variant="outline" className="bg-green-50 text-green-700">
                              {txn.category}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-amber-50 text-amber-700">
                              Uncategorized
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {txn.transaction_type === 'credit' ? (
                            <Badge className="bg-green-100 text-green-700">
                              <ArrowUpRight className="h-3 w-3 mr-1" />
                              Credit
                            </Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-700">
                              <ArrowDownRight className="h-3 w-3 mr-1" />
                              Debit
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className={`text-right font-medium ${
                          txn.transaction_type === 'credit' ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {txn.transaction_type === 'credit' ? '+' : '-'}
                          {formatCurrency(txn.amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
